import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  FileText,
  Loader2,
  Save,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  teacherGetAttempt,
  teacherGetHomework,
  teacherGradeAttempt,
  teacherGradeHomework,
  teacherGradingQueue,
} from "@/lib/grading";

function HomeworkGrader({ id, onClose, onDone }) {
  const [sub, setSub] = useState(null);
  const [marks, setMarks] = useState({ awarded: 8, total: 10 });
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    teacherGetHomework(id).then((s) => {
      setSub(s);
      setFeedback(s.teacher_feedback || "");
      setMarks({
        awarded: s.awarded_marks ?? 8,
        total: s.total_marks ?? 10,
      });
    });
  }, [id]);
  if (!sub) return <Loader2 className="h-4 w-4 animate-spin" />;
  const save = async () => {
    if (marks.awarded > marks.total) {
      toast.error("Awarded marks cannot exceed total");
      return;
    }
    setBusy(true);
    try {
      await teacherGradeHomework(id, {
        awarded_marks: Number(marks.awarded),
        total_marks: Number(marks.total),
        feedback,
      });
      toast.success("Saved");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-stone-200" data-testid="homework-grader">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-amber-600" /> {sub.chapter_title}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="space-y-3 list-decimal pl-5 text-sm">
          {sub.answers.map((a) => (
            <li key={a.question_id}>
              <div className="font-medium">{a.question_text}</div>
              <div className="text-stone-700 whitespace-pre-wrap pl-1 mt-1">
                {a.answer_text || (
                  <span className="text-stone-400">(blank)</span>
                )}
              </div>
            </li>
          ))}
        </ol>
        <Separator />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Awarded marks</Label>
            <Input
              type="number"
              min={0}
              value={marks.awarded}
              onChange={(e) =>
                setMarks((p) => ({ ...p, awarded: e.target.value }))
              }
              data-testid="hw-awarded-input"
            />
          </div>
          <div>
            <Label className="text-xs">Total marks</Label>
            <Input
              type="number"
              min={1}
              value={marks.total}
              onChange={(e) =>
                setMarks((p) => ({ ...p, total: e.target.value }))
              }
              data-testid="hw-total-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Feedback</Label>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            data-testid="hw-feedback-input"
          />
        </div>
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="hw-save-button"
          >
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            <Save className="mr-1 h-4 w-4" /> Save grade
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TestGrader({ id, onClose, onDone }) {
  const [att, setAtt] = useState(null);
  const [scores, setScores] = useState({}); // qid -> score
  const [total, setTotal] = useState(10);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    teacherGetAttempt(id).then((a) => {
      setAtt(a);
      setFeedback(a.teacher_feedback || "");
      const init = {};
      (a.subjective_answers || []).forEach((s) => {
        init[s.question_id] = 0;
      });
      setScores(init);
      setTotal(a.subjective_total || Math.max((a.subjective_answers || []).length * 5, 5));
    });
  }, [id]);

  if (!att) return <Loader2 className="h-4 w-4 animate-spin" />;

  const save = async () => {
    const sum = Object.values(scores).reduce(
      (s, v) => s + Number(v || 0),
      0
    );
    if (sum > Number(total)) {
      toast.error(`Sum of scores (${sum}) exceeds total (${total})`);
      return;
    }
    setBusy(true);
    try {
      await teacherGradeAttempt(id, {
        subjective_scores: Object.entries(scores).map(([qid, s]) => ({
          question_id: qid,
          score: Number(s || 0),
        })),
        subjective_total: Number(total),
        feedback,
      });
      toast.success("Saved");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-stone-200" data-testid="test-grader">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-amber-600" />{" "}
            {att.chapter_title}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Card className="bg-stone-50 border-stone-200">
          <CardContent className="py-3 text-sm space-y-1">
            <div className="font-medium">
              MCQ (auto): {att.mcq_score}/{att.mcq_total}
            </div>
            <ul className="text-xs text-stone-600 space-y-0.5">
              {(att.per_mcq_breakdown || []).map((b, i) => (
                <li key={b.question_id} className="flex items-center gap-1">
                  {b.is_correct ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-600" />
                  )}
                  Q{i + 1}: {b.is_correct ? "correct" : "wrong"}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <div>
          <div className="text-xs uppercase font-semibold text-stone-500 mb-2">
            Subjective answers
          </div>
          <ol className="space-y-3 list-decimal pl-5 text-sm">
            {(att.subjective_answers || []).map((a) => (
              <li key={a.question_id}>
                <div className="font-medium">{a.question_text}</div>
                <div className="text-stone-700 whitespace-pre-wrap pl-1 mt-1">
                  {a.answer_text || (
                    <span className="text-stone-400">(blank)</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <Label className="text-xs">Score</Label>
                  <Input
                    type="number"
                    min={0}
                    className="w-20"
                    value={scores[a.question_id] ?? ""}
                    onChange={(e) =>
                      setScores((p) => ({
                        ...p,
                        [a.question_id]: e.target.value,
                      }))
                    }
                    data-testid={`subj-score-${a.question_id}`}
                  />
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Subjective total</Label>
            <Input
              type="number"
              min={1}
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              data-testid="subj-total-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Feedback</Label>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            data-testid="test-feedback-input"
          />
        </div>
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="test-save-button"
          >
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            <Save className="mr-1 h-4 w-4" /> Save grade
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TeacherGrading() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // {type, id}
  const navigate = useNavigate();

  const refresh = () =>
    teacherGradingQueue()
      .then(setQueue)
      .finally(() => setLoading(false));
  useEffect(() => {
    refresh();
  }, []);

  const homework = queue.filter((q) => q.type === "homework");
  const tests = queue.filter((q) => q.type === "test");

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="teacher-grading"
    >
      <DashboardHeader subtitle="Teacher · Grading center" />
      <main className="mx-auto max-w-5xl px-6 py-6 space-y-4">
        <BackButton to="/teacher" />
        <h1 className="text-2xl font-semibold">Grading center</h1>
        <p className="text-sm text-stone-500">
          Pending submissions across your courses. Oldest-first.
        </p>

        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
        ) : open ? (
          open.type === "homework" ? (
            <HomeworkGrader
              id={open.id}
              onClose={() => setOpen(null)}
              onDone={() => {
                setOpen(null);
                refresh();
              }}
            />
          ) : (
            <TestGrader
              id={open.id}
              onClose={() => setOpen(null)}
              onDone={() => {
                setOpen(null);
                refresh();
              }}
            />
          )
        ) : (
          <Tabs defaultValue="homework">
            <TabsList>
              <TabsTrigger value="homework" data-testid="grading-tab-homework">
                Homework ({homework.length})
              </TabsTrigger>
              <TabsTrigger value="tests" data-testid="grading-tab-tests">
                Tests ({tests.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="homework" className="space-y-2 mt-3">
              {homework.length === 0 ? (
                <p className="text-sm text-stone-500">Nothing pending. 🎉</p>
              ) : (
                homework.map((q) => (
                  <Row key={q.id} item={q} onOpen={() => setOpen(q)} />
                ))
              )}
            </TabsContent>
            <TabsContent value="tests" className="space-y-2 mt-3">
              {tests.length === 0 ? (
                <p className="text-sm text-stone-500">
                  All tests are graded. 🎉
                </p>
              ) : (
                tests.map((q) => (
                  <Row key={q.id} item={q} onOpen={() => setOpen(q)} />
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

function Row({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left border border-stone-200 rounded-md px-3 py-2 bg-white hover:bg-stone-50 flex items-center justify-between"
      data-testid={`grading-row-${item.id}`}
    >
      <div>
        <div className="text-sm font-medium">{item.chapter_title}</div>
        <div className="text-xs text-stone-500">
          {item.student_name} · {item.course_title}
        </div>
      </div>
      <div className="text-xs text-stone-500">
        {new Date(item.submitted_at).toLocaleString()}
      </div>
    </button>
  );
}
